import { useMemo } from "react";
import { create } from "qrcode";

/** The spec's four-module quiet zone. Without it, readers miss the corners. */
const QUIET_ZONE = 4;

/**
 * Renders an otpauth URL as something a phone camera can read.
 *
 * Drawn as a single path rather than a rect per module: a code this size is
 * roughly 2,400 modules, and there is no reason to make the browser lay out
 * 2,400 elements.
 *
 * Deliberately dark-on-white even though the rest of the app is dark. Inverted
 * codes scan on some readers and not others, and a code that fails on the
 * customer's phone is a worse outcome than a light panel in a dark screen.
 */
export function QrCode({ value, label }: { value: string; label: string }) {
  const { path, span } = useMemo(() => {
    const { modules } = create(value, { errorCorrectionLevel: "M" });
    const segments: string[] = [];

    for (let row = 0; row < modules.size; row++) {
      for (let column = 0; column < modules.size; column++) {
        if (modules.get(row, column)) {
          segments.push(`M${column + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`);
        }
      }
    }

    return { path: segments.join(""), span: modules.size + QUIET_ZONE * 2 };
  }, [value]);

  return (
    <svg
      viewBox={`0 0 ${span} ${span}`}
      role="img"
      aria-label={label}
      className="h-44 w-44 rounded bg-white p-1"
      // Modules are exact squares; smoothing them blurs the edges a reader
      // uses to find the grid.
      shapeRendering="crispEdges"
    >
      <path d={path} fill="#000000" />
    </svg>
  );
}
